import { MouseEvent, useState } from "react";
import Image from "next/image";
import { Button, Tooltip } from "@heathmont/moon-core-tw";
import { ControlsEye } from "@heathmont/moon-icons-tw";
import MultiCarousel from "@/components/MultiCarousel";
import { useEntities } from "@/contexts/entities/EntitiesContext";
import Analytics from "@/lib/analytics";
import colors from "@/lib/static/colors";
import {
  Activity,
  Competency,
  LearnerOutcome,
  SubCompetency,
} from "@/lib/models";
import { createShareUrlWithSelectionQueryParam } from "@/lib/utils";
import Share from "../icons/Share";
import ActivityCard from "../ActivityCard";
import { HtmlRenderer } from "../HtmlRenderer";
import { CompetencyPrevNextButtons } from "./CompetencyPrevNextButtons";
import { CompetencyDetailSmallNavigation } from "./CompetencyDetailSmallNavigation";
import {
  ResourcePath,
  SavedResourcesAction,
  SavedResourcesToast,
} from "./SavedResourcesToast";
import { ShareToast } from "./ShareToast";
import { SubCompetencyList } from "./SubCompetencyList";
import {
  ButtonSizes,
  ButtonTypes,
  IconTypes,
  StyledButton,
} from "@/components/StyledButton";
import { ErrorToast } from "./ErrorToast";

type CompetencyDetailsProps = {
  className?: string;
  competency?: Competency;
  learnerOutcome: LearnerOutcome;
  changeCompetency: (competencyId: string) => Promise<void>;
};

type SavedResourcesToastState = {
  isOpen: boolean;
  currentAction: SavedResourcesAction | null;
};

export const CompetencyDetails = ({
  className,
  competency,
  changeCompetency,
  learnerOutcome,
}: CompetencyDetailsProps) => {
  const [hasLinkCopied, setHasLinkCopied] = useState(false);
  const [savedResourcesToastState, setSavedResourcesToastState] =
    useState<SavedResourcesToastState>({
      isOpen: false,
      currentAction: null,
    });
  const [isErrorToastOpen, setIsErrorToastOpen] = useState<boolean>(false);
  const {
    getSubCompetency,
    getActivitiesForCompetency,
    getEntityHierarchyFromId,
  } = useEntities();

  if (!competency) {
    return <div className={className}></div>;
  }

  const subCompetencies = competency.subCompetencies.map(
    getSubCompetency,
  ) as SubCompetency[];

  const competencyActivities = getActivitiesForCompetency(competency.id);

  const handleCopyLinkClick = (id: string) => (e: MouseEvent) => {
    e.preventDefault();
    Analytics.handleShareLinkClick(getEntityHierarchyFromId(id));
    navigator.clipboard.writeText(createShareUrlWithSelectionQueryParam(id));
    setHasLinkCopied(true);
  };

  const handleExternalResourceLinkClick = (id: string, url: string) => () => {
    Analytics.handleExternalResourceLinkClick({
      entityHierarchy: getEntityHierarchyFromId(id),
      url,
    });
  };

  const handleCloseSavedResourcesToast = () => {
    setSavedResourcesToastState((prev) => ({ ...prev, isOpen: false }));
  };

  const handleAddActivityToSavedResources = (activity: Activity) => () => {
    setSavedResourcesToastState({
      isOpen: true,
      currentAction: SavedResourcesAction.Add,
    });
    Analytics.handleAddActivityToSavedResourcesEvent(
      activity.urlSlug,
      activity.name,
    );
  };

  const handleRemoveActivityFromSavedResources = (activity: Activity) => () => {
    setSavedResourcesToastState({
      isOpen: true,
      currentAction: SavedResourcesAction.Remove,
    });
    Analytics.handleRemoveActivityFromSavedResourcesEvent(
      activity.urlSlug,
      activity.name,
    );
  };

  const handleUpdateSavedResourcesError = () => {
    setIsErrorToastOpen(true);
  };

  return (
    <div className={className}>
      <div className="flex flex-col justify-between">
        <div className="flex h-[340px] flex-col items-center medium:h-[180px] medium:flex-row">
          <CompetencyDetailSmallNavigation
            className="medium:hidden"
            learnerOutcome={learnerOutcome}
            competency={competency}
            changeCompetency={changeCompetency}
          />

          {competency.competencyCard && competency.icon.sourceUrl && (
            <div className="medium:ml-4 medium:mr-6 large:ml-6">
              <Image
                src={competency.icon.sourceUrl!}
                width={174}
                height={201}
                alt={competency.name}
                className="h-[152px] w-[132px] large:h-[201px] large:w-[174px]"
              />
            </div>
          )}
          <div className="mt-6 grow large:w-[494px]">
            <div className="text-center font-roobert text-xs font-normal uppercase text-neutral-800 medium:text-left ">
              Competency
            </div>
            <h2 className="text-center font-roobert text-[32px] font-medium leading-tight medium:text-left large:text-[40px]">
              {competency.name}
            </h2>
            <div className="mt-1 text-center font-roobert text-lg font-normal text-neutral-800 medium:text-left medium:text-xl">
              <HtmlRenderer html={competency.descriptionRtf} />
            </div>
            {competency.externalResourcesUrl && (
              <Button
                as="a"
                target="_blank"
                href={competency.externalResourcesUrl}
                onClick={handleExternalResourceLinkClick(
                  competency.id,
                  competency.externalResourcesUrl,
                )}
                className="mt-3.5 hidden rounded-sm bg-black px-5 py-2 font-bold text-white shadow-moon-sm medium:inline-flex"
                aria-label="explore resources"
              >
                <ControlsEye width={24} height={24} />
                Explore Resources
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="medium:pl-4 large:pl-6 large:pr-8">
        <div className="mt-9 flex justify-end medium:mt-0">
          {competency.externalResourcesUrl && (
            <Button
              as="a"
              target="_blank"
              href={competency.externalResourcesUrl}
              onClick={handleExternalResourceLinkClick(
                competency.id,
                competency.externalResourcesUrl,
              )}
              className="mr-4 inline-flex rounded-sm bg-black px-5 py-2 font-bold text-white shadow-moon-sm medium:hidden"
              aria-label="explore resources"
            >
              <ControlsEye width={24} height={24} />
              Explore Resources
            </Button>
          )}
          <div className="mt-14 medium:mt-10 large:mt-20">
            <Tooltip>
              <Tooltip.Trigger className="flex">
                <StyledButton
                  buttonType={ButtonTypes.Tertiary}
                  size={ButtonSizes.Small}
                  iconType={IconTypes.Left}
                  onClick={handleCopyLinkClick(competency.id)}
                  className="medium:mt-6"
                  aria-label="Copy Competency link to clipboard"
                >
                  <Share fill={colors["BG-60"]} />
                  <span>Share</span>
                </StyledButton>
              </Tooltip.Trigger>
              <Tooltip.Content className="bg-black text-white">
                Copy Competency link to clipboard
                <Tooltip.Arrow className="bg-black" />
              </Tooltip.Content>
            </Tooltip>
          </div>
        </div>

        {!!competency.definitionDescription && (
          <>
            <div className="-mt-5 mb-6 text-2xl">Overview</div>
            <div className="mb-8 font-roobert text-base font-normal leading-tight text-neutral-600">
              {competency.definitionDescription}
            </div>
          </>
        )}

        <SubCompetencyList
          subCompetencies={subCompetencies}
          competencyName={competency.name}
        />

        {/* Activities Related to Competency */}

        {competencyActivities.length > 0 && (
          <>
            <div className="my-10 w-full border-t border-tab-border medium:my-[48px]"></div>
            <div className="mb-4 font-roobert text-2xl font-normal text-black">
              Activities
            </div>
            <div className="mb-6 font-roobert text-base font-normal leading-tight text-neutral-600">
              Classroom activities that spark interest and amplify learning in
              any content area.
            </div>
            <MultiCarousel
              options={{ loop: false }}
              containerClassName="auto-cols-auto w-[361px] small:w-[448px]"
              slides={competencyActivities.map((activity, index) => {
                return (
                  <ActivityCard
                    key={index}
                    activity={activity}
                    index={index}
                    className="embla__slide w-[361px] small:h-[460px] small:w-[448px]"
                    onAddToSavedResources={handleAddActivityToSavedResources(
                      activity,
                    )}
                    onRemoveFromSavedResources={handleRemoveActivityFromSavedResources(
                      activity,
                    )}
                    onUpdateSavedResourcesError={
                      handleUpdateSavedResourcesError
                    }
                  />
                );
              })}
            />
          </>
        )}
        <CompetencyPrevNextButtons
          competency={competency}
          changeCompetency={changeCompetency}
          learnerOutcome={learnerOutcome}
        />
      </div>

      <SavedResourcesToast
        isOpen={savedResourcesToastState.isOpen}
        currentAction={savedResourcesToastState.currentAction}
        resourcePath={ResourcePath.Activity}
        handleClose={handleCloseSavedResourcesToast}
      />
      <ShareToast
        isOpen={hasLinkCopied}
        handleClose={() => setHasLinkCopied((prevState) => !prevState)}
      />
      <ErrorToast
        isOpen={isErrorToastOpen}
        handleClose={() => setIsErrorToastOpen(false)}
      />
    </div>
  );
};
